/**
 * Google Cast (Chromecast) sender integration - the only module that talks
 * to the Cast Application Framework SDK directly (a CDN global, loaded via
 * client/src/index.html's <script async> - see global.d.ts for its ambient
 * types, same pattern as hls.js/socket.io). Exposes a small PlaybackDriver
 * interface with two implementations - one over the local <video>/<audio>,
 * one over CAF's RemotePlayer/RemotePlayerController - so Player.ts's and
 * musicBar/MusicBar.ts's own sync logic (sendLocalState/applyRemoteState,
 * applyMusicState) can read/write "the player" without caring which one is
 * actually driving playback right now. See docs/architecture.md's "Casting
 * to a Chromecast" section for the full design and why this is the only
 * new abstraction that was needed.
 */

/** What Player.ts's sync logic needs from a player, whether that's the
 *  local <video> element or a Chromecast's RemotePlayer. */
export interface PlaybackDriver {
  currentTime: number;
  readonly paused: boolean;
  /** 0 until known (matches <video>'s own `duration` before metadata
   *  loads - NaN there, normalized to 0 here so a caller can use it
   *  directly as a divisor/percentage without its own NaN check). */
  readonly duration: number;
  play(): void;
  pause(): void;
}

/** The local <video> or <audio>, wrapped in the same shape a cast driver
 *  uses - trivial, since a media element already looks like this either
 *  way (HTMLMediaElement is their shared base - see Player.ts for the
 *  video case, musicBar/MusicBar.ts for audio). */
export function localDriver(media: HTMLMediaElement): PlaybackDriver {
  return {
    get currentTime() { return media.currentTime; },
    set currentTime(seconds: number) { media.currentTime = seconds; },
    get paused() { return media.paused; },
    get duration() { return media.duration || 0; },
    play() { media.play().catch(() => { }); },
    pause() { media.pause(); }
  };
}

let initialized = false;
let currentlyAvailable = false;
const availabilityListeners = new Set<(available: boolean) => void>();
const sessionEndedListeners = new Set<() => void>();

function setAvailable(available: boolean): void {
  currentlyAvailable = available;
  availabilityListeners.forEach((cb) => cb(available));
}

/** Only ever called once the SDK has confirmed it's loaded (see initCast
 *  below) - window.cast/window.chrome.cast are guaranteed present here,
 *  per the Cast SDK's own contract for __onGCastApiAvailable(true). */
function onSdkReady(): void {
  const framework = window.cast!.framework;
  const context = framework.CastContext.getInstance();
  context.setOptions({
    receiverApplicationId: window.chrome!.cast!.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
    autoJoinPolicy: window.chrome!.cast!.AutoJoinPolicy.ORIGIN_SCOPED
  });

  // The event listener alone only reports the *next* change - a device
  // already on the network before this runs needs this one explicit
  // read too, or the cast button would stay hidden until something else
  // happened to change availability.
  setAvailable(context.getCastState() !== framework.CastState.NO_DEVICES_AVAILABLE);
  context.addEventListener(framework.CastContextEventType.CAST_STATE_CHANGED, (event) => {
    setAvailable(event.castState !== framework.CastState.NO_DEVICES_AVAILABLE);
  });
  context.addEventListener(framework.CastContextEventType.SESSION_STATE_CHANGED, (event) => {
    if (event.sessionState === framework.SessionState.SESSION_ENDED) {
      sessionEndedListeners.forEach((cb) => cb());
    }
  });
}

/**
 * Registers the CAF-required global callback and configures the default
 * receiver (no custom receiver app, no Google Cast Developer Console
 * registration needed - see docs/architecture.md). Safe to call more than
 * once (e.g. every time a video party mounts): only the first call
 * actually does anything. Handles both possible orderings against the
 * SDK's own <script async> tag - the callback firing after this runs (the
 * normal case), or the SDK having already loaded and fired it before this
 * ever got a chance to register (a fast network, or a second party opened
 * in the same page load).
 */
export function initCast(): void {
  if (initialized) return;
  initialized = true;

  if (window.chrome?.cast?.isAvailable) {
    onSdkReady();
  } else {
    window.__onGCastApiAvailable = (isAvailable) => { if (isAvailable) onSdkReady(); };
  }
}

/**
 * Reports whether a Cast device is currently on the network - immediately
 * with whatever's already known (false until the SDK's finished loading
 * and found one), then again on every change. Returns an unsubscribe
 * function - callers MUST call it from their own cleanup (see Player.ts's
 * router-driven cleanup pattern - this app has a real history of leaked
 * listeners/timers, see docs/architecture.md) so this module's listener
 * set never outlives the component that registered it.
 */
export function onCastAvailabilityChanged(listener: (available: boolean) => void): () => void {
  availabilityListeners.add(listener);
  listener(currentlyAvailable);
  return () => availabilityListeners.delete(listener);
}

/** Fires whenever the current Cast session ends, however that happens -
 *  the user stopping it from this page, from the Chromecast's own remote,
 *  or the receiver dropping off the network. Same unsubscribe contract as
 *  onCastAvailabilityChanged above. */
export function onCastSessionEnded(listener: () => void): () => void {
  sessionEndedListeners.add(listener);
  return () => sessionEndedListeners.delete(listener);
}

/**
 * Starts casting `contentUrl` (an absolute HLS URL carrying its own
 * castToken - see Player.ts and CastTokenService) and returns a
 * PlaybackDriver over the resulting RemotePlayer. Throws if the user
 * cancels the device picker or the load fails - the caller is expected to
 * just leave the local <video> driving in that case, same as any other
 * "the thing the user tried didn't work" path in this app.
 * `onRemoteStateChanged` fires whenever the TV side's own play/pause/seek
 * state changes (the physical remote, or the receiver's own on-screen
 * controls) - Player.ts uses it exactly like the local <video>'s
 * 'play'/'pause'/'seeked' events, to know when to re-broadcast as host.
 */
export async function startCast(contentUrl: string, title: string, onRemoteStateChanged: () => void): Promise<PlaybackDriver> {
  console.info(`Trying to cast ${title} from ${contentUrl}`);
  const framework = window.cast!.framework;
  const chromeCast = (window.chrome as any)?.cast;

  const context = framework.CastContext.getInstance();
  await context.requestSession();
  const session = context.getCurrentSession();
  if (!session) {
    throw new Error('Aucune session Cast active après requestSession().');
  }

  // 1. Initialisation de MediaInfo avec le MIME HLS universel
  const mediaInfo = new chromeCast.media.MediaInfo(contentUrl, 'application/x-mpegurl');
  if (chromeCast.media.StreamType) {
    mediaInfo.streamType = chromeCast.media.StreamType.BUFFERED;
  }

  // 2. Structuration des metadonnées
  if (chromeCast.media.GenericMediaMetadata) {
    const metadata = new chromeCast.media.GenericMediaMetadata();
    metadata.title = title;
    mediaInfo.metadata = metadata;
  } else {
    mediaInfo.metadata = { title };
  }

  // 3. Préparation et envoi de la requête de chargement
  const request = new chromeCast.media.LoadRequest(mediaInfo);
  request.autoplay = true;

  await session.loadMedia(request as any);

  const player = new framework.RemotePlayer();
  const controller = new framework.RemotePlayerController(player);

  controller.addEventListener(framework.RemotePlayerEventType.IS_PAUSED_CHANGED, onRemoteStateChanged);
  controller.addEventListener(framework.RemotePlayerEventType.CURRENT_TIME_CHANGED, onRemoteStateChanged);
  console.info(`Casting ${title} from ${contentUrl}`);
  return {
    get currentTime() { return player.currentTime; },
    set currentTime(seconds: number) { player.currentTime = seconds; controller.seek(); },
    get paused() { return player.isPaused; },
    get duration() { return player.duration || 0; },
    play() { if (player.isPaused) controller.playOrPause(); },
    pause() { if (!player.isPaused) controller.playOrPause(); }
  };
}

/** Ends the current Cast session (if any) - called when the user clicks
 *  "stop casting". A harmless no-op if nothing is currently cast. */
export function stopCast(): void {
  window.cast?.framework.CastContext.getInstance().getCurrentSession()?.endSession(true);
}
